import type { PageConfig } from 'liyu-pc-base/components/remotePages/typing';
import { reactive } from 'vue';
import { getApiPermission, getUrl } from 'liyu-pc-base/components/remotePages/utils';
import { cloneDeep } from 'lodash';
import { usePageConfigStore } from 'liyu-pc-base/init-plugin';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useEnv } from 'liyu-pc-base/init-plugin';
import { useGlobMap } from 'liyu-pc-base/init-plugin';
import { useCellComponentMap, useCellFormatExcelValueMap } from 'liyu-pc-base/init-plugin';
import { modify_by_i18n_config } from './i18n_config';

const mergeBtnList = (config: any[], customConfig: any[]) => {
  if (!customConfig) return config;
  const result = cloneDeep(config || []);

  // 创建一个按钮名称到索引的映射
  const btnMap = new Map();
  result.forEach((btn, index) => {
    delete btn._key;
    btnMap.set(btn.name, index);
  });

  // 遍历自定义配置进行合并
  let last_order = 0;
  customConfig.forEach(customBtn => {
    if (!customBtn.order) {
      customBtn.order = last_order + 1;
    }
    last_order = customBtn.order;
    customBtn._key = `custom_${customBtn.name}_${Math.random()}`;
    const existingIndex = btnMap.get(customBtn.name);

    if (existingIndex !== undefined) {
      // 如果按钮已存在，递归合并子按钮
      const existingBtn = result[existingIndex];
      if (customBtn.children && existingBtn.children) {
        existingBtn.children = mergeBtnList(existingBtn.children, customBtn.children);
      }
      const temp = cloneDeep(customBtn);
      delete temp.children;
      // 合并其他属性
      Object.assign(existingBtn, temp);
    } else {
      const temp = cloneDeep(customBtn);
      result.push(temp);
      if (temp.children) {
        temp.children = mergeBtnList([], temp.children);
      }
    }
  });

  return result;
};

const mergeDataFunc = {
  btn_list: mergeBtnList,
  head_btn_list: mergeBtnList,
  batch_update_list: mergeBtnList,
};

const mergeData = (config: any, customConfig: any) => {
  const res = cloneDeep(config);
  // Object.assign(mergedData, customConfig);
  Object.keys(customConfig).forEach(key => {
    if (mergeDataFunc[key]) {
      res[key] = mergeDataFunc[key](config[key], customConfig[key]);
    } else {
      res[key] = customConfig[key];
    }
  });

  Object.assign(config, res);
};
const overrideBaseColumns = (config: any, overrideConfig: any) => {
  if (!config) {
    return;
  }
  config.forEach(item => {
    const dataIndex = item.dataIndex;
    if (overrideConfig[dataIndex]) {
      for (const key in overrideConfig[dataIndex]) {
        if (overrideConfig[dataIndex][key] === null) {
          delete item[key];
        } else {
          item[key] = overrideConfig[dataIndex][key];
        }
      }
    }
  });
  return config;
};
const overrideBaseSearchConditionConfigList = (config: any, overrideConfig: any) => {
  if (!config) {
    return;
  }
  config.forEach(item => {
    const group = item.group;
    if (!group) {
      return;
    }
    group.forEach(groupItem => {
      const field_name = groupItem.field_name;
      if (overrideConfig[field_name]) {
        for (const key in overrideConfig[field_name]) {
          if (overrideConfig[field_name][key] === null) {
            delete groupItem[key];
          } else {
            groupItem[key] = overrideConfig[field_name][key];
          }
        }
      }
    });
  });
  return config;
};
const overrideSearchConfig = (config: any, overrideConfig: any) => {
  if (!config) {
    return;
  }
  const fields = config.fields;
  const rules = config.rules || {};
  const model = config.model || {};
  const options = config.options || {};
  if (!fields) {
    return;
  }
  fields.forEach(field => {
    const name = field.name;
    if (overrideConfig.fields?.[name]) {
      for (const key in overrideConfig.fields[name]) {
        if (overrideConfig.fields[name][key] === null) {
          delete field[key];
        } else {
          field[key] = overrideConfig.fields[name][key];
        }
      }
    }
    if (overrideConfig.rules?.[name] !== undefined) {
      rules[name] = overrideConfig.rules[name];
    }
    if (overrideConfig.model?.[name] !== undefined) {
      model[name] = overrideConfig.model[name];
    }
    if (overrideConfig.options?.[name] !== undefined) {
      options[name] = overrideConfig.options[name];
    }
  });
  const newConfig = {
    fields,
    rules,
    model,
    options,
  };
  Object.assign(config, newConfig);
  return config;
};
const overrideDataFunc = {
  baseColumns: overrideBaseColumns,
  base_search_condition_config_list: overrideBaseSearchConditionConfigList,
  searchConfig: overrideSearchConfig,
};

const overrideData = (config: any, overrideConfig: any) => {
  const res = cloneDeep(config);
  // Object.assign(mergedData, customConfig);
  Object.keys(overrideConfig).forEach(key => {
    if (overrideDataFunc[key]) {
      config[key] = overrideDataFunc[key](config[key], overrideConfig[key]);
    }
  });
  return res;
};
const processBaseColumns = (config: any) => {
  if (!config || !Array.isArray(config)) {
    return config;
  }
  const cellComponentMap = useCellComponentMap();
  const cellFormatExcelValueMap = useCellFormatExcelValueMap();
  for (const item of config) {
    if (item.cellComponent && !item.formatExcelValue) {
      const cellComponentData = cellComponentMap[item.cellComponent];
      if (cellComponentData) {
        const defaultExcelFormat = cellComponentData.defaultExcelFormat;
        const cellFormatExcelValue = cellFormatExcelValueMap[defaultExcelFormat];
        if (cellFormatExcelValue) {
          item.formatExcelValue = cellFormatExcelValue.flag;
        }
      }
    }
  }
  return config;
};
const processDataFunc = {
  baseColumns: processBaseColumns,
};

const processData = (config: any) => {
  Object.keys(config).forEach(key => {
    if (processDataFunc[key]) {
      config[key] = processDataFunc[key](config[key]);
    }
  });
};
const ts_config_map: { [key: string]: any } = {};
const init_ts_config = () => {
  if (Object.keys(ts_config_map).length > 0) {
    return;
  }
  const allTsConfigs: any = import.meta.glob('liyu-pc-base/components/remotePages/configs/*/*.ts', {
    eager: true,
  });
  const customTsConfigs: any = useGlobMap('remotePages');
  Object.assign(allTsConfigs, customTsConfigs);
  Object.keys(allTsConfigs).forEach(full_file_name => {
    const file_name_list = full_file_name.split('/');
    const file_name = file_name_list[file_name_list.length - 1].replace('.ts', '');
    if (file_name.endsWith('-config')) {
      return;
    }
    ts_config_map[file_name] = allTsConfigs[full_file_name].default;
  });
};
export const getViewSettingsAndPageConfig = async ({
  org_id,
  user_id,
  app_flag,
  modalType,
  sort_fields,
  is_cache,
  history_id,
}: {
  org_id: string;
  user_id: number;
  app_flag: string;
  modalType: string;
  sort_fields: string[];
  is_cache: boolean;
  history_id?: number | null;
}) => {
  const env = useEnv();
  const [viewSettings, defaultAndServerConfig] = await Promise.all([
    getApiData(
      {
        org_id,
        user_id,
        view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
        view_menu__flag: modalType,
        history_id,
        sort_fields,
        is_cache,
      },
      '/view/get_view_settings_for_user',
    ),
    getDefaultAndServerConfig({
      org_id,
      app_flag,
      modalType,
      history_id,
    }),
  ]);

  return {
    ...viewSettings,
    defaultAndServerConfig: defaultAndServerConfig,
  };
};

export const getDefaultAndServerConfig = async ({
  org_id,
  app_flag,
  modalType,
  history_id,
}: {
  org_id: string;
  app_flag: string;
  modalType: string;
  history_id?: number | null;
}) => {
  const env = useEnv();
  const model_name = modalType.split('-')[1];
  const flag = modalType + '_config';
  const serverConfigParams: any = {
    flag,
    org_id,
  };
  if (history_id) {
    serverConfigParams.history_id = history_id;
  }
  const defaultConfigParams: any = {
    org_id,
    model_name,
    get_i18n_config: !!env.VITE_APP_ENABLE_ZH_EN,
  };
  const overrideConfigParams: any = {
    org_id,
    model_name,
    api_url: `/${app_flag}/query_${model_name}_list`,
  };
  const result: any = {};
  const skip_app_flag = [];
  if (skip_app_flag.includes(app_flag)) {
    const serverRes = await getApiData(
      serverConfigParams,
      '/sys/get_global_custom_data_by_flag_without_org_id',
    );
    result.defaultConfig = {};
    result.override = null;
    result.serverConfig = serverRes || {};
    return result;
  } else {
    const [defaultRes, overrideRes, serverRes] = await Promise.all([
      getApiData(
        defaultConfigParams,
        `/${app_flag}/dev_get_column_config_and_remote_config_json`,
        false,
        {
          skip_error: true,
        },
      ),
      getApiData(overrideConfigParams, `/${app_flag}/override_column_config`, false, {
        skip_error: true,
      }),
      getApiData(serverConfigParams, '/sys/get_global_custom_data_by_flag_without_org_id'),
    ]);
    result.defaultConfig = defaultRes || {};
    result.override = overrideRes || {};
    result.serverConfig = serverRes || {};
    return result;
  }
};
const processPermissionForSuffix = (config: any, suffix: string) => {
  if (!config || !suffix) {
    return;
  }
  const appendSuffix = (val: string) => {
    if (typeof val === 'string' && val && !val.endsWith(`-${suffix}`)) {
      return `${val}-${suffix}`;
    }
    return val;
  };
  if (config.titleList) {
    const keys = ['is_create', 'is_update', 'is_remove', 'is_recover'];
    keys.forEach(key => {
      if (config.titleList[key]) {
        config.titleList[key] = appendSuffix(config.titleList[key]);
      }
    });
  }
  const processBtnList = (list: any[]) => {
    if (!list || !Array.isArray(list)) return;
    list.forEach(item => {
      if (item.authority) {
        item.authority = appendSuffix(item.authority);
      }
      if (item.children) {
        processBtnList(item.children);
      }
    });
  };
  processBtnList(config.btn_list);
  processBtnList(config.head_btn_list);
  processBtnList(config.batch_update_list);
};

export const getPageConfig = (
  app_flag: string,
  modalType: string,
  defaultAndServerConfig: {
    defaultConfig: any;
    serverConfig: any;
    override?: any;
  },
  t: any,
  options?: {
    use_store?: Boolean;
    to_i18n?: Boolean;
    enable_btn?: Boolean;
  },
): PageConfig => {
  const { use_store = true, to_i18n = false, enable_btn = false } = options || {};
  init_ts_config();
  const pageConfigStore = use_store ? usePageConfigStore() : null;
  let ts_config_data: any;
  let model_app = undefined;
  let model_name = undefined;
  let model_suffix = undefined;
  const list = modalType.split('-');
  if (list.length >= 2) {
    model_app = list[0];
    model_name = list[1];
    model_suffix = list[2];
  } else {
    console.error(`modalType:${modalType}结构错误`);
    return undefined;
  }
  if (pageConfigStore) {
    if (defaultAndServerConfig) {
      pageConfigStore.cleanPageConfig(modalType);
    } else {
      const cachedConfig = pageConfigStore.getCachedConfig(modalType);
      if (cachedConfig) {
        return cachedConfig;
      }
    }
  }
  let ts_config = ts_config_map[modalType];
  let is_suffix = false;
  if (!ts_config && model_suffix) {
    ts_config = ts_config_map[`${model_app}-${model_name}`];
    if (ts_config) {
      is_suffix = true;
    }
  }
  if (!ts_config) {
    const tmp_config = ts_config_map[`${model_name}`];
    if (tmp_config) {
      ts_config = tmp_config;
    }
  }
  if (!ts_config) {
    console.log('remotePages3没有组件：' + modalType);
    if (pageConfigStore) {
      pageConfigStore.setCustomConfig(modalType, {});
    }
  } else {
    ts_config_data = cloneDeep(ts_config(t, { model_app }));
    if (is_suffix) {
      processPermissionForSuffix(ts_config_data, model_suffix);
    }
    if (pageConfigStore) {
      pageConfigStore.setCustomConfig(modalType, ts_config_data);
    }
  }
  const titleList: any = reactive({
    title: '',
    addModalTitle: '新建',
    updateTitle: '修改',
    is_create: getApiPermission(modalType, 'create_'), // 是否有【新建】按钮
    is_update: getApiPermission(modalType, 'update_'), // 是否有【修改】按钮
    is_remove: getApiPermission(modalType, 'delete_'), // 是否有【删除】按钮
    is_recover: getApiPermission(modalType, 'recover_'), // 是否有【恢复】按钮
  });
  const searchConfig: any = {
    rules: reactive({}),
    fields: [],
    settings: {},
    model: reactive({}),
    options: {},
  };
  const defaultConfig: any = {
    is_selection: false,
    addData: getUrl(modalType, ['create_', '']),
    updateData: getUrl(modalType, ['update_', '']),
    recoverData: getUrl(modalType, ['recover_', '']),
    removeData: getUrl(modalType, ['delete_', '']),
    countData: getUrl(modalType, ['query_', '_count']),
    queryData: getUrl(modalType, ['query_', '_list']),
    queryConditionGroupData: getUrl(modalType, ['query_', '_group']),
    getData: getUrl(modalType, ['get_', '']),

    searchConfig: searchConfig,
    base_search_condition_config_list: null,
    default_search_condition_config_list: null,
    condition_group_config_list: null,
    is_show_advantage: true,
    is_show_normal_search: false,
    is_only_show_advantage: true,
    is_only_show_normal_search: false,
    init_request: true,
    processData: null,
    refresh_type: '手动', // '手动', '5s', '10s', '30s'
    required_condition: [],
    globalSearch: null,

    formModalConfig: null,
    updateFormModalConfig: null,
    titleList: titleList,
    api_fields: null,
    btn_list: null,
    head_btn_list: null,
    batch_update_list: null,
    event_obj: undefined,

    is_operation: [true, true, true, true],
    baseColumns: null,
    column_list_change: null,
    processColumn: null,
    customCell: null,
    customRow: null,
    customHeaderCell: null,

    is_pagination: true,
    pageSize: 50,
    page_size_options: [
      '10',
      '20',
      '50',
      '100',
      '200',
      '500',
      '1000',
      '3000',
      '10000',
      '50000',
      '60000',
      '70000',
    ], // 默认页面选项
    is_page_mode_change: false,
    is_stripe: null,

    isView: true,
    default_hide_view: false,
    rowKey: 'id',
    rowSelection: { type: 'checkbax', checkStrictly: false },
    expandedRowRender: null,
    expandedRowRenderComponent: null,
    headerComponent: null,
    expandFunc: null,
    summaryData: null,
    summaryType: null,
    default_column_field: ['is_active'],
    i18n: null,
    get_db_alias: null,
    advanced_base_conditions: [],
    enable_view_types: ['normal', 'pivot', 'pivot_charts'],
    default_view_type: 'normal',
    pivot_table_max_records: 10000,
    detailConfigs: {},
    query_params: {},
  };
  if (defaultAndServerConfig) {
    Object.assign(defaultConfig, defaultAndServerConfig.defaultConfig);
    if (model_suffix) {
      processPermissionForSuffix(defaultConfig, model_suffix);
    }
    if (pageConfigStore) {
      pageConfigStore.setDefaultConfig(modalType, defaultConfig);
    }
  } else {
    const cachedDefaultConfig = pageConfigStore && pageConfigStore.getDefaultConfig(modalType);
    if (cachedDefaultConfig) {
      Object.assign(defaultConfig, cachedDefaultConfig);
    }
  }
  const mergedConfig = cloneDeep(defaultConfig);
  let serverConfig = null;
  if (defaultAndServerConfig) {
    serverConfig = defaultAndServerConfig.serverConfig;
    if (pageConfigStore) {
      pageConfigStore.setServerConfig(modalType, serverConfig);
    }
  } else {
    serverConfig = pageConfigStore && pageConfigStore.getServerConfig(modalType);
  }
  if (
    !serverConfig?.default_column_field &&
    serverConfig?.searchConfig?.default_column_field &&
    serverConfig?.searchConfig?.default_column_field?.length > 0
  ) {
    serverConfig.default_column_field = serverConfig.searchConfig.default_column_field;
  }
  if (!enable_btn && serverConfig) {
    delete serverConfig['btn_list'];
    delete serverConfig['head_btn_list'];
    delete serverConfig['batch_update_list'];
  }

  Object.assign(mergedConfig, serverConfig);
  let overrideConfig = null;
  if (defaultAndServerConfig) {
    overrideConfig = defaultAndServerConfig.override;
    if (pageConfigStore && overrideConfig) {
      pageConfigStore.setOverrideConfig(modalType, overrideConfig);
    }
  } else {
    overrideConfig = pageConfigStore && pageConfigStore.getOverrideConfig(modalType);
  }
  if (overrideConfig) {
    overrideData(mergedConfig, overrideConfig);
  }
  if (ts_config_data) {
    mergeData(mergedConfig, ts_config_data);
    if (ts_config_data?.default_column_field && ts_config_data?.default_column_field?.length > 0) {
      mergedConfig.default_column_field = ts_config_data?.default_column_field;
    } else if (
      ts_config_data?.searchConfig?.default_column_field &&
      ts_config_data?.searchConfig?.default_column_field?.length > 0
    ) {
      mergedConfig.default_column_field = ts_config_data?.searchConfig?.default_column_field;
    }
  }
  if (pageConfigStore) {
    pageConfigStore.setMergedConfig(modalType, mergedConfig);
  }
  if (mergedConfig.is_stripe) {
    const processData = raw_data_list => {
      let index = 0;
      const obj_dict = {};
      raw_data_list.forEach(item => {
        item.color_index0 = 1;
        if (obj_dict?.[item?.[mergedConfig.is_stripe]] == undefined) {
          obj_dict[item[mergedConfig.is_stripe]] = index;
          index += 1;
          item.color_index0 = 0;
        }
        item.color_index = obj_dict[item[mergedConfig.is_stripe]];
      });
      return raw_data_list;
    };

    const customRow = record => {
      if (record.color_index0 == 1) {
        return { style: { background: 'rgba(85,162,235,0.3)' } };
      }
      if (record.color_index % 2 == 0) {
        return { style: { background: '#FFFFFE' } };
      } else {
        return { style: { background: '#FAFAFA' } };
      }
    };
    mergedConfig.processData = processData;
    mergedConfig.customRow = customRow;
  }
  processData(mergedConfig);

  let result = mergedConfig;
  if (to_i18n) {
    result = modify_by_i18n_config(mergedConfig, t, modalType);
  }
  if (pageConfigStore) {
    pageConfigStore.setCachedConfig(modalType, result);
  }
  return result;
};

const conditionsHistory = {};

export const setConditionsHistory = (modalType: string, conditions: any) => {
  conditionsHistory[modalType] = cloneDeep(conditions);
};

export const getConditionsHistory = (modalType: string) => {
  const res = cloneDeep(conditionsHistory[modalType]);
  return res;
};
